"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowDown, Github, Linkedin, Twitter } from "lucide-react";
import {
	motion,
	useReducedMotion,
	useScroll,
	useSpring,
	useTransform,
} from "framer-motion";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import {
	SiDocker,
	SiGithub,
	SiGmail,
	SiGo,
	SiLinkedin,
	SiNestjs,
	SiNextdotjs,
	SiPhp,
	SiReaddotcv,
	SiTailwindcss,
	SiTypescript,
} from "react-icons/si";
import type { HomeSocialLinks } from "@/lib/home-social-links";

const leftStagger = {
	hidden: {},
	show: {
		transition: {
			delayChildren: 0.52,
			staggerChildren: 0.12,
		},
	},
} as const;

const leftItem = {
	hidden: { opacity: 0, y: 22, filter: "blur(8px)" },
	show: {
		opacity: 1,
		y: 0,
		filter: "blur(0px)",
		transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
	},
} as const;

const visualReveal = {
	hidden: { opacity: 0, x: 32, filter: "blur(10px)" },
	show: {
		opacity: 1,
		x: 0,
		filter: "blur(0px)",
		transition: { duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] },
	},
} as const;

const heroNodes = [
	{
		label: "TypeScript",
		icon: (
			<svg
				className="h-7 w-7"
				viewBox="0 0 32 32"
				fill="none"
				aria-hidden="true"
			>
				<rect width="32" height="32" rx="3" fill="#3178c6" />
				<path
					d="M7.2 13.65h10.1v2.55h-3.6v9.15h-2.9V16.2H7.2v-2.55Zm11.05 10.95v-2.85c.48.39 1.05.7 1.7.93.65.23 1.29.34 1.93.34.37 0 .7-.03.98-.1.28-.07.51-.16.7-.28.19-.12.33-.26.42-.42.09-.16.14-.34.14-.53 0-.26-.07-.48-.22-.67a2.07 2.07 0 0 0-.6-.55 6.24 6.24 0 0 0-.9-.48c-.34-.15-.71-.31-1.1-.47a5.53 5.53 0 0 1-2.25-1.55 3.38 3.38 0 0 1-.74-2.2c0-.69.14-1.28.42-1.77.28-.49.66-.9 1.14-1.21.49-.31 1.05-.54 1.69-.68.64-.14 1.32-.22 2.03-.22.7 0 1.32.04 1.86.13.54.08 1.04.21 1.49.38v2.66a4.46 4.46 0 0 0-1.58-.72 6.07 6.07 0 0 0-1.72-.25c-.34 0-.65.03-.92.1-.28.06-.51.15-.7.27a1.5 1.5 0 0 0-.45.41c-.11.16-.16.34-.16.54 0 .22.06.41.18.58.12.17.29.33.51.48.22.15.49.29.8.43.31.14.67.29 1.06.45.51.21.97.44 1.38.69.41.25.76.53 1.05.85.29.32.52.69.68 1.1.16.41.24.89.24 1.43 0 .74-.14 1.36-.42 1.86-.28.5-.67.9-1.16 1.2-.49.3-1.06.52-1.72.65-.65.13-1.34.19-2.07.19-.74 0-1.44-.06-2.1-.19a6.35 6.35 0 0 1-1.77-.56Z"
					fill="#fff"
				/>
			</svg>
		),
		size: "h-16 w-16",
		position: "left-[26%] top-[44%]",
		rotate: "-rotate-12",
		depth: 110,
		drift: -26,
	},
	{
		label: "NestJS",
		icon: <SiNestjs className="h-8 w-8 text-[#e0234e]" />,
		size: "h-20 w-20",
		position: "left-[44%] top-[31%]",
		rotate: "rotate-6",
		depth: 148,
		drift: 18,
	},
	{
		label: "Next.js",
		icon: <SiNextdotjs className="h-7 w-7 text-[#e9edf5]" />,
		size: "h-16 w-16",
		position: "left-[59%] top-[45%]",
		rotate: "rotate-12",
		depth: 124,
		drift: 24,
	},
	{
		label: "PHP",
		icon: <SiPhp className="h-5 w-5 text-[#e9edf5]" />,
		size: "h-11 w-11",
		position: "left-[36%] top-[58%]",
		rotate: "-rotate-6",
		depth: 88,
		drift: -16,
	},
	{
		label: "Python",
		icon: (
			<svg
				className="h-5 w-5"
				viewBox="0 0 32 32"
				fill="none"
				aria-hidden="true"
			>
				<path
					fill="#3776ab"
					d="M15.9 3.2c-6.1 0-5.7 2.7-5.7 2.7v2.8h5.8v.8H7.9S4 9 4 15.1s3.4 5.9 3.4 5.9h2v-2.9s-.1-3.4 3.4-3.4h5.8s3.2.1 3.2-3.1V6.2s.5-3-5.9-3Zm-3.2 1.8c.6 0 1.1.5 1.1 1.1s-.5 1.1-1.1 1.1-1.1-.5-1.1-1.1.5-1.1 1.1-1.1Z"
				/>
				<path
					fill="#ffd43b"
					d="M16.1 28.8c6.1 0 5.7-2.7 5.7-2.7v-2.8H16v-.8h8.1s3.9.4 3.9-5.6-3.4-5.9-3.4-5.9h-2v2.9s.1 3.4-3.4 3.4h-5.8s-3.2-.1-3.2 3.1v5.4s-.5 3 5.9 3Zm3.2-1.8c-.6 0-1.1-.5-1.1-1.1s.5-1.1 1.1-1.1 1.1.5 1.1 1.1-.5 1.1-1.1 1.1Z"
				/>
			</svg>
		),
		size: "h-10 w-10",
		position: "left-[55%] top-[60%]",
		rotate: "rotate-3",
		depth: 74,
		drift: 12,
	},
	{
		label: "Golang",
		icon: <SiGo className="h-4 w-4 text-[#e9edf5]" />,
		size: "h-10 w-10",
		position: "left-[33%] top-[34%]",
		rotate: "rotate-9",
		depth: 92,
		drift: -18,
	},
	{
		label: "Tailwind CSS",
		icon: <SiTailwindcss className="h-4 w-4 text-[#e9edf5]" />,
		size: "h-10 w-10",
		position: "left-[64%] top-[33%]",
		rotate: "-rotate-6",
		depth: 90,
		drift: 20,
	},
	{
		label: "Docker",
		icon: <SiDocker className="h-4 w-4 text-[#e9edf5]" />,
		size: "h-10 w-10",
		position: "left-[29%] top-[53%]",
		rotate: "-rotate-12",
		depth: 82,
		drift: -26,
	},
	{
		label: "GitHub",
		icon: <SiGithub className="h-4 w-4 text-[#e9edf5]" />,
		size: "h-10 w-10",
		position: "left-[66%] top-[57%]",
		rotate: "rotate-8",
		depth: 78,
		drift: 22,
	},
] as const;

export default function HomeHeroParallax({
	socialLinks,
}: {
	socialLinks: HomeSocialLinks;
}) {
	const sectionRef = useRef<HTMLElement | null>(null);
	const [isAboutHovered, setIsAboutHovered] = useState(false);
	const [heroReady, setHeroReady] = useState(false);
	const shouldReduceMotion = useReducedMotion();
	const { scrollYProgress } = useScroll({
		target: sectionRef,
		offset: ["start start", "end start"],
	});

	const smoothedProgress = useSpring(scrollYProgress, {
		stiffness: 90,
		damping: 20,
		mass: 0.35,
	});

	const textY = useTransform(
		smoothedProgress,
		[0, 1],
		[0, shouldReduceMotion ? 0 : -96],
	);
	const textOpacity = useTransform(
		smoothedProgress,
		[0, 1],
		[1, shouldReduceMotion ? 1 : 0.72],
	);
	const visualY = useTransform(
		smoothedProgress,
		[0, 1],
		[0, shouldReduceMotion ? 0 : -235],
	);
	const visualRotate = useTransform(
		smoothedProgress,
		[0, 1],
		[0, shouldReduceMotion ? 0 : -6],
	);
	const visualScale = useTransform(
		smoothedProgress,
		[0, 1],
		[1, shouldReduceMotion ? 1 : 0.88],
	);
	const backgroundY = useTransform(
		smoothedProgress,
		[0, 1],
		[0, shouldReduceMotion ? 0 : -70],
	);
	const overlayY = useTransform(
		smoothedProgress,
		[0, 1],
		[0, shouldReduceMotion ? 0 : 80],
	);
	const gridY = useTransform(
		smoothedProgress,
		[0, 1],
		[0, shouldReduceMotion ? 0 : 96],
	);
	const gopherY = useTransform(
		smoothedProgress,
		[0, 1],
		[0, shouldReduceMotion ? 0 : -132],
	);

	useEffect(() => {
		if (shouldReduceMotion) {
			setHeroReady(true);
			return;
		}

		setHeroReady(false);

		const timeoutId = window.setTimeout(() => {
			setHeroReady(true);
		}, 100);

		return () => window.clearTimeout(timeoutId);
	}, [shouldReduceMotion]);

	return (
		<motion.section
			ref={sectionRef}
			className="relative min-h-screen overflow-hidden lg:min-h-0"
			initial={false}
			animate={{ opacity: 1, y: 0 }}
			transition={{
				duration: shouldReduceMotion ? 0.3 : 0.95,
				ease: [0.22, 1, 0.36, 1],
			}}
		>
			<div className="pointer-events-none absolute inset-0">
				<motion.div
					style={{ y: backgroundY }}
					className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(229,233,240,0.14),transparent_24%),radial-gradient(circle_at_82%_12%,rgba(174,180,190,0.1),transparent_20%)]"
				/>
				<motion.div
					style={{ y: overlayY }}
					className="absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.04)_8%,transparent_34%,transparent_66%,rgba(255,255,255,0.03)_92%)]"
				/>
				<motion.div
					style={{ y: gridY }}
					className="absolute bottom-8 left-0 right-0 h-28 bg-[radial-gradient(circle,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[length:18px_18px] opacity-20"
				/>
				<div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent via-[#0b0d12]/75 to-[#0b0d12]" />
			</div>

			<div className="mx-auto grid min-h-[100svh] w-full max-w-6xl content-center gap-10 px-4 pb-16 pt-24 sm:px-6 sm:min-h-0 sm:pb-16 sm:pt-24 lg:grid-cols-[minmax(0,34rem)_1fr] lg:pb-16 lg:pt-24">
				<motion.div
					style={{ y: textY, opacity: textOpacity }}
					className="relative z-10 flex flex-col justify-center"
				>
					<motion.div
						initial="hidden"
						animate={heroReady ? "show" : "hidden"}
						variants={leftStagger}
					>
						<motion.div
							variants={leftItem}
							className="relative inline-flex w-fit items-center gap-2.5 overflow-hidden rounded-full px-3.5 py-2 sm:gap-3 sm:px-4 sm:py-2.5"
						>
							<span className="pointer-events-none absolute inset-y-0 left-0 w-[130%] rounded-full bg-[linear-gradient(90deg,rgba(198,206,218,0.2)_0%,rgba(122,133,148,0.15)_34%,rgba(11,13,18,0.1)_68%,rgba(11,13,18,0)_100%)]" />
							<span className="pointer-events-none absolute left-2 top-1/2 h-[72%] w-20 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(224,231,240,0.11)_0%,rgba(224,231,240,0)_72%)]" />
							<span className="relative z-10 h-2.5 w-2.5 shrink-0 rounded-full bg-[#e3e8f1] shadow-[0_0_10px_rgba(227,232,241,0.45)]" />
							<div className="relative z-10 text-[0.78rem] leading-[1.18] text-zinc-300 sm:text-[0.9rem]">
								<p className="text-zinc-400">Based in</p>
								<p className="mt-0.5 font-medium text-zinc-100">
									Tanjung Pinang — Available Worldwide
								</p>
							</div>
						</motion.div>

						<motion.h1
							variants={leftItem}
							className="font-rethink mt-8 max-w-[11ch] text-[3rem] font-semibold leading-[0.9] tracking-[-0.05em] text-white sm:mt-9 sm:max-w-[14ch] sm:text-5xl lg:text-[4.1rem]"
						>
							Muhammad{" "}
							<span className="bg-[linear-gradient(102deg,#f5f7fb_4%,#c7cdd7_28%,#8f98a6_47%,#f0f3f8_63%,#9ea8b8_78%,#f5f7fb_96%)] bg-clip-text text-transparent drop-shadow-[0_0_20px_rgba(207,214,224,0.26)]">
								Nurfatkhur
							</span>{" "}
							Rahman
						</motion.h1>
						<motion.p
							variants={leftItem}
							className="mt-4 max-w-[22rem] text-[0.9rem] leading-[1.55] text-zinc-300 sm:mt-5 sm:max-w-[34rem] sm:text-[1.05rem] sm:leading-[1.72]"
						>
							I turn ideas into reliable software systems and continuosly refine
							how complex problems are solved through practical engineering.
						</motion.p>
						<motion.p
							variants={leftItem}
							className="mt-3 max-w-[22rem] text-[0.82rem] leading-[1.55] text-zinc-400 sm:max-w-none sm:text-[0.95rem]"
						>
							Tools I enjoy:{" "}
							<span className="font-medium text-zinc-200">
								Next.js, Laravel, Node.js, React Native
							</span>
						</motion.p>

						<motion.div
							variants={leftItem}
							className="relative mt-7 flex flex-wrap items-center gap-2.5 sm:mt-8 sm:gap-3"
						>
							<span
								className={`pointer-events-none absolute h-7 w-16 rounded-full bg-[linear-gradient(90deg,rgba(221,229,240,0.22),rgba(221,229,240,0.09)_50%,rgba(221,229,240,0)_100%)] blur-[2px] transition-all duration-500 ${
									isAboutHovered
										? "translate-x-[15.2rem] opacity-100"
										: "translate-x-[13.2rem] opacity-0"
								}`}
							/>
							<Link
								href="/projects"
								className={`group relative inline-flex items-center gap-2.5 overflow-visible rounded-2xl px-4 py-2.5 text-[0.9rem] text-zinc-100 backdrop-blur-xl transition-all duration-400 sm:px-5 sm:text-[0.95rem] ${
									isAboutHovered
										? "border border-white/8 bg-[linear-gradient(160deg,rgba(255,255,255,0.02),rgba(255,255,255,0))] shadow-[0_2px_10px_rgba(3,8,20,0.22)]"
										: "border border-white/12 bg-[linear-gradient(155deg,rgba(210,220,235,0.1),rgba(80,94,115,0.1)_38%,rgba(22,30,44,0.32)_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.22),inset_0_-1px_0_rgba(255,255,255,0.05),0_10px_28px_rgba(3,8,20,0.42)]"
								} hover:-translate-y-0.5`}
							>
								<span
									className={`pointer-events-none absolute inset-0 z-0 transition-opacity duration-150 ${
										isAboutHovered ? "opacity-0" : "opacity-100"
									}`}
								>
									<svg
										className="h-full w-full"
										viewBox="0 0 100 40"
										preserveAspectRatio="none"
										aria-hidden="true"
									>
										<rect
											className="showcase-trace-path"
											x="1.2"
											y="1.2"
											width="97.6"
											height="37.6"
											rx="13"
											pathLength="100"
											fill="none"
											stroke="rgba(225, 232, 242, 0.62)"
											strokeWidth="1.2"
											strokeLinecap="round"
										/>
									</svg>
								</span>
								<span
									className={`pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_22%_12%,rgba(255,255,255,0.2),transparent_52%)] transition-all duration-500 ${
										isAboutHovered
											? "-translate-x-2 opacity-35"
											: "translate-x-0 opacity-58"
									}`}
								/>
								<span className="relative z-10">Showcase</span>
								<span
									className={`relative z-10 inline-flex h-7 w-7 items-center justify-center rounded-full text-zinc-200 transition-all duration-300 sm:h-8 sm:w-8 ${
										isAboutHovered
											? "border border-white/12 bg-white/5 shadow-none"
											: "border border-white/25 bg-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.35)]"
									}`}
								>
									<ArrowDown
										className={`h-3.5 w-3.5 transition-transform duration-300 ${
											isAboutHovered ? "translate-y-0" : "translate-y-0.5"
										}`}
									/>
								</span>
							</Link>
							<Link
								href="/about"
								onMouseEnter={() => setIsAboutHovered(true)}
								onMouseLeave={() => setIsAboutHovered(false)}
								className={`inline-flex items-center rounded-2xl px-4 py-2.5 text-[0.9rem] text-zinc-100 backdrop-blur-xl transition-all duration-500 sm:px-5 sm:text-[0.95rem] ${
									isAboutHovered
										? "translate-x-0.5 border border-white/12 bg-[linear-gradient(155deg,rgba(210,220,235,0.16),rgba(80,94,115,0.14)_38%,rgba(22,30,44,0.34)_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.28),inset_0_-1px_0_rgba(255,255,255,0.06),0_10px_24px_rgba(3,8,20,0.35)]"
										: "border border-white/10 bg-[linear-gradient(160deg,rgba(255,255,255,0.02),rgba(255,255,255,0))]"
								} hover:border-white/20 hover:bg-white/[0.03]`}
							>
								More about me
							</Link>
						</motion.div>

						<motion.div
							variants={leftItem}
							className="mt-8 flex items-center gap-4 text-zinc-600 sm:mt-10 sm:gap-5"
						>
							{socialLinks.github ? (
								<a
									href={socialLinks.github}
									target="_blank"
									rel="noopener noreferrer"
									aria-label="GitHub"
									className="cursor-ne-resize cursor-apple-ne-resize transition hover:text-zinc-200"
								>
									<SiGithub className="h-5 w-5" />
								</a>
							) : null}
							{socialLinks.linkedin ? (
								<a
									href={socialLinks.linkedin}
									target="_blank"
									rel="noopener noreferrer"
									aria-label="LinkedIn"
									className="cursor-ne-resize cursor-apple-ne-resize transition hover:text-zinc-200"
								>
									<SiLinkedin className="h-5 w-5" />
								</a>
							) : null}
							{socialLinks.email ? (
								<a
									href={socialLinks.email}
									target="_blank"
									rel="noopener noreferrer"
									aria-label="Email"
									className="cursor-ne-resize cursor-apple-ne-resize transition hover:text-zinc-200"
								>
									<SiGmail className="h-5 w-5" />
								</a>
							) : null}
							{socialLinks.cv ? (
								<a
									href={socialLinks.cv}
									target="_blank"
									rel="noopener noreferrer"
									aria-label="CV"
									className="cursor-ne-resize cursor-apple-ne-resize transition hover:text-zinc-200"
								>
									<SiReaddotcv className="h-5 w-5" />
								</a>
							) : null}
						</motion.div>
					</motion.div>
				</motion.div>

				<motion.div
					style={{ y: visualY, rotate: visualRotate, scale: visualScale }}
					className="relative hidden min-h-[38rem] translate-x-3 will-change-transform lg:block"
					initial="hidden"
					animate={heroReady ? "show" : "hidden"}
					variants={visualReveal}
				>
					<div className="absolute right-[-0.5rem] top-8 h-[34rem] w-[26.8rem] rotate-[34deg] rounded-[2.5rem] border border-[#d5dbe5]/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0))]" />
					<div className="absolute right-[-3rem] top-[4.3rem] h-[34rem] w-[26.8rem] rotate-[34deg] rounded-[2.5rem] border border-[#d5dbe5]/8 bg-[linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0))]" />
					<motion.div
						style={
							{
								"--progress": shouldReduceMotion ? 0 : smoothedProgress,
							} as CSSProperties
						}
						className="absolute right-0 top-6 h-[34rem] w-[27.2rem] rotate-[34deg] rounded-[2.5rem] border border-[#d5dbe5]/12 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0))] shadow-[0_0_0_1px_rgba(255,255,255,0.05)]"
					>
						<div className="absolute inset-4 rounded-[2rem] border border-white/5 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.03),transparent_60%)]" />
						<div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:26px_26px] opacity-18" />
						<div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(45deg,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:44px_44px] opacity-10" />
						<div className="absolute inset-[4.5rem] rounded-[50%] border border-white/5" />
						<div className="absolute inset-[6rem] rounded-[50%] border border-white/5" />
						<div className="absolute inset-[7.5rem] rounded-[50%] border border-white/5" />
						<div className="absolute inset-[9rem] rounded-[50%] border border-white/5" />
						<div className="absolute inset-[10.5rem] rounded-[50%] border border-white/5" />
						<div className="absolute inset-[12rem] rounded-[50%] border border-white/5" />
						<div className="absolute bottom-[5.3rem] left-1/2 h-[11rem] w-[14rem] -translate-x-1/2 rounded-[50%] border border-white/8 border-t-transparent" />
						<div className="absolute bottom-[4.7rem] left-1/2 h-[13rem] w-[17rem] -translate-x-1/2 rounded-[50%] border border-white/8 border-t-transparent" />
						<div className="absolute bottom-[4.1rem] left-1/2 h-[15rem] w-[20rem] -translate-x-1/2 rounded-[50%] border border-white/7 border-t-transparent" />

						{heroNodes.map((node) => (
							<div
								key={node.label}
								aria-label={node.label}
								style={
									{
										"--depth": node.depth,
										"--drift": node.drift,
									} as CSSProperties
								}
								className={`absolute ${node.position} ${node.rotate} ${node.size} [transform:translate3d(calc(var(--progress)*var(--drift)*1px),calc(var(--progress)*var(--depth)*-1px),0)] will-change-transform flex items-center justify-center rounded-[1.35rem] border border-[#d9dfe8]/18 bg-[linear-gradient(155deg,rgba(33,40,53,0.98),rgba(23,28,36,0.95))] text-sm font-semibold text-zinc-200 shadow-[0_14px_30px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.18)]`}
							>
								<div className="absolute inset-0 rounded-[1.35rem] bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.14),transparent_55%)]" />
								<span className="relative">{node.icon}</span>
							</div>
						))}
					</motion.div>

					<motion.div
						style={{ y: gopherY }}
						className="absolute left-[10.8rem] top-[50.5%] flex rotate-[34deg] items-center rounded-full border border-[#d5dbe5]/20 bg-[linear-gradient(160deg,rgba(30,36,48,0.95),rgba(18,23,33,0.9))] px-5 py-3 text-xl text-zinc-300 shadow-[0_0_30px_rgba(0,0,0,0.28),inset_0_1px_0_rgba(255,255,255,0.14)]"
					>
						<Image
							src="/images/gopher.png"
							alt="Golang gopher"
							width={42}
							height={42}
							className="h-10 w-10 object-contain drop-shadow-[0_4px_10px_rgba(0,0,0,0.35)]"
						/>
					</motion.div>
				</motion.div>
			</div>
		</motion.section>
	);
}
